import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Plus, X, FloppyDisk, PencilSimple, ArrowCounterClockwise } from "@phosphor-icons/react";
import { useSector } from "@/lib/sectorContext";
import VariableInserter from "@/components/VariableInserter";

// Catálogo de opções curadas — todas as listas servem oficina
// mecânica e concessionária. O VALOR enviado pro backend é a
// frase em PT-BR (LABEL), porque o LLM consome essa string direto
// no system prompt; não há "código interno". Resultado: o dono
// só clica, nunca digita.
const TONES = [
  { value: "consultivo_premium", label: "Consultivo Premium" },
  { value: "tecnico_direto", label: "Técnico Direto" },
  { value: "acolhedor_casual", label: "Acolhedor Casual" },
  { value: "vendedor_classico", label: "Vendedor Clássico" },
  { value: "concierge_luxo", label: "Concierge Luxo" },
];

const STANCES = [
  { value: "consultive", label: "Consultivo (apresenta, não empurra)" },
  { value: "hard_sell", label: "Comercial Direto (vai pra fechar)" },
  { value: "neutral", label: "Neutro" },
];

const COMMUNICATION_STYLES = [
  "Direto e técnico",
  "Caloroso e consultivo",
  "Formal e respeitoso",
  "Descontraído e próximo",
  "Premium e refinado",
  "Energético e vendedor",
  "Objetivo e prático",
];

const BUSINESS_FOCUS = [
  "Captar novos clientes",
  "Recorrência e fidelização",
  "Receita imediata (fechar agora)",
  "Atendimento premium / experiência",
  "Eficiência operacional (entrada e saída rápida)",
  "Reativar clientes inativos",
  "Crescimento por indicação e boca a boca",
];

const CUSTOMER_POSTURES = [
  "Tratamento VIP — cliente é tratado como dono do carro mais caro do dia",
  "Próximo e amigo — vibe descontraída, sem formalidade",
  "Consultor especialista — guia técnico que explica tudo",
  "Profissional cordial — direto, sem firulas, mas educado",
  "Concierge premium — atenção total, respostas rápidas, mimo no detalhe",
  "Anfitrião acolhedor — recebe como se fosse visita em casa",
];

const ESCALATION_POLICIES = [
  "Escalar para humano logo no primeiro sinal de complexidade",
  "Escalar apenas em reclamação, urgência ou negociação avançada",
  "Escalar quando o cliente pedir explicitamente humano",
  "Nunca escalar automaticamente — IA resolve sempre que possível",
  "Escalar fora do horário comercial",
  "Escalar imediatamente em qualquer menção a problema grave ou ameaça legal",
];

const NEGOTIATION_POLICIES = [
  "Nunca informa preço — sempre direciona para a equipe",
  "Não negocia — preço firme",
  "Até 5% de desconto para fechar na hora",
  "Até 10% de desconto para fechar na hora",
  "Até 15% de desconto para fechar na hora",
  "Negocia caso a caso — escala para humano decidir",
  "Negocia agressivamente — prioridade é fechar",
];

const UPSELL_BEHAVIORS = [
  "Sem upsell — apenas o que o cliente pediu",
  "Upsell sutil — sugere 1 serviço complementar quando faz sentido",
  "Upsell consultivo — explica benefícios de combos quando relevante",
  "Upsell ativo — sempre apresenta a opção premium primeiro",
  "Upsell pós-venda — só sugere depois do serviço executado",
  "Upsell em janela quente — só quando cliente já demonstrou interesse",
];

const empty = {
  tone_of_voice: "consultivo_premium",
  service_style: "",
  use_emojis: true,
  communication_style: "",
  upsell_behavior: "",
  escalation_policy: "",
  negotiation_policy: "",
  customer_posture: "",
  commercial_stance: "consultive",
  business_focus: "",
  differentials: [],
  specialties: [],
  forbidden_phrases: [],
  preferred_phrases: [],
  policies: [],
};

function ChipList({ items, onAdd, onRemove, placeholder, testId }) {
  const [v, setV] = useState("");
  const add = () => {
    const t = v.trim();
    if (!t) return;
    onAdd(t);
    setV("");
  };
  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex flex-wrap gap-1.5">
        {(items || []).map((it, i) => (
          <span
            key={`${it}-${i}`}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-900 text-white text-[12px]"
          >
            {it}
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="hover:opacity-70"
              aria-label="remover"
              data-testid={`${testId}-remove-${i}`}
            >
              <X size={10} weight="bold" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={v}
          onChange={(e) => setV(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
          data-testid={`${testId}-input`}
        />
        <button
          type="button"
          onClick={add}
          className="px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-[13px] inline-flex items-center gap-1"
          data-testid={`${testId}-add`}
        >
          <Plus size={12} weight="bold" /> Adicionar
        </button>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {hint && <p className="text-[12px] text-slate-400 -mt-0.5">{hint}</p>}
      {children}
    </div>
  );
}

/**
 * OptionSelect — curated dropdown for identity behaviour fields.
 *
 * The VALUE saved in the DB is the option's label string (PT-BR phrase),
 * so the orchestrator can drop it directly into the persona prompt.
 *
 * If a legacy free-text value was saved before this redesign, we surface
 * it at the top of the list as "Personalizado: …" so the operator's old
 * input is preserved AND visible — they can keep it or pick a curated
 * option to replace it. Zero data loss.
 */
function OptionSelect({ value, options, onChange, testId, placeholder = "Selecione…" }) {
  const isLegacy = value && !options.includes(value);
  return (
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
    >
      <option value="" disabled>{placeholder}</option>
      {isLegacy && (
        <option value={value}>{`Personalizado: ${value.slice(0, 60)}${value.length > 60 ? "…" : ""}`}</option>
      )}
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

export default function IdentityTab({ onChange, onToast }) {
  const [data, setData] = useState(empty);
  // Agent identity lives in `inbound_settings` (collection historically
  // managed by /api/reception/settings). We absorb display/name/company
  // here. The on/off switch (Modo Teste vs Produção) used to live here
  // too but moved to its own page in the **Configuração** menu group
  // (Feb/2026) — see `/app/pages/TestMode.jsx`.
  const [agent, setAgent] = useState({
    agent_display_mode: "brand_agent",
    agent_name: "",
    company_name: "",
    smart_welcome_default_template: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [idRes, recRes] = await Promise.all([
        api.get("/operacao/identity"),
        api.get("/reception/settings"),
      ]);
      if (idRes.data?.configured === false) {
        setData(empty);
      } else {
        setData({ ...empty, ...idRes.data });
      }
      const r = recRes.data || {};
      setAgent({
        agent_display_mode: r.agent_display_mode || "brand_agent",
        agent_name: r.agent_name || "",
        company_name: r.company_name || "",
        smart_welcome_default_template: r.smart_welcome_default_template || "",
      });
    } catch (e) {
      onToast?.("Não foi possível carregar a identidade.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setData((prev) => ({ ...prev, [k]: v }));
  const setAg = (k, v) => setAgent((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.tenant_id; delete payload.id;
      delete payload.updated_at; delete payload.updated_by;
      delete payload.configured;
      await Promise.all([
        api.put("/operacao/identity", payload),
        api.put("/reception/settings", {
          agent_display_mode: agent.agent_display_mode,
          agent_name: agent.agent_name || "",
          company_name: agent.company_name || "",
          smart_welcome_default_template: agent.smart_welcome_default_template || "",
        }),
      ]);
      onToast?.("Identidade atualizada.");
      // Iter-71.1: broadcast so the LivePreview side-panel can
      // refetch the negotiation_policy without forcing a full refresh.
      try {
        window.dispatchEvent(new CustomEvent("agentus:identity-updated"));
      } catch (_e) { /* SSR guard — no-op */ }
      onChange?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Erro ao salvar.", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-400">Carregando…</div>;

  return (
    <div className="space-y-8" data-testid="identity-tab">
      {/* ── Apresentação do agente — quem ele diz que é no WhatsApp ── */}
      <section className="rounded-2xl border border-slate-100 p-5 space-y-4" data-testid="identity-agent-section">
        <header className="flex items-baseline justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 tracking-tight">Apresentação do agente</h3>
            <p className="text-[12px] text-slate-500 mt-0.5">Como ele se identifica na primeira mensagem.</p>
          </div>
        </header>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Como o agente aparece" hint="Marca do negócio, assistente com nome próprio ou sem assinatura.">
            <select
              data-testid="identity-agent-display-mode"
              value={agent.agent_display_mode}
              onChange={(e) => setAg("agent_display_mode", e.target.value)}
              className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
            >
              <option value="brand_agent">Marca da empresa</option>
              <option value="named_assistant">Assistente com nome próprio</option>
              <option value="silent">Silencioso (sem assinatura)</option>
            </select>
          </Field>

          {agent.agent_display_mode === "named_assistant" && (
            <Field label="Nome do assistente" hint="Ex.: Ana, Lucas, Bia. Visível na assinatura.">
              <input
                type="text"
                data-testid="identity-agent-name"
                value={agent.agent_name}
                onChange={(e) => setAg("agent_name", e.target.value)}
                placeholder="Ex.: Bia"
                className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
              />
            </Field>
          )}

          <Field label="Nome da empresa" hint="Como o agente cita o negócio.">
            <input
              type="text"
              data-testid="identity-company-name"
              value={agent.company_name}
              onChange={(e) => setAg("company_name", e.target.value)}
              placeholder="Ex.: AutoCare Premium"
              className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] focus:border-slate-900 focus:outline-none"
            />
          </Field>
        </div>

        <AgentIdentityPreview agent={agent} setAg={setAg} />
      </section>

      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Tom de voz">
          <select
            data-testid="identity-tone"
            value={data.tone_of_voice}
            onChange={(e) => set("tone_of_voice", e.target.value)}
            className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
          >
            {TONES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Postura comercial">
          <select
            data-testid="identity-stance"
            value={data.commercial_stance}
            onChange={(e) => set("commercial_stance", e.target.value)}
            className="w-full px-3 py-2 rounded-full border border-slate-200 text-[13px] bg-white focus:border-slate-900 focus:outline-none"
          >
            {STANCES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </Field>

        <Field label="Estilo de comunicação" hint="Como o Agentus se expressa. Escolha o que mais combina com a casa.">
          <OptionSelect
            testId="identity-communication-style"
            value={data.communication_style}
            options={COMMUNICATION_STYLES}
            onChange={(v) => set("communication_style", v)}
          />
        </Field>

        <Field label="Foco do negócio" hint="O que a IA prioriza ao responder.">
          <OptionSelect
            testId="identity-business-focus"
            value={data.business_focus}
            options={BUSINESS_FOCUS}
            onChange={(v) => set("business_focus", v)}
          />
        </Field>

        <Field label="Postura com cliente" hint="Como tratar quem chega no WhatsApp.">
          <OptionSelect
            testId="identity-customer-posture"
            value={data.customer_posture}
            options={CUSTOMER_POSTURES}
            onChange={(v) => set("customer_posture", v)}
          />
        </Field>

        <Field label="Política de escalonamento" hint="Quando a IA passa o atendimento para humano.">
          <OptionSelect
            testId="identity-escalation-policy"
            value={data.escalation_policy}
            options={ESCALATION_POLICIES}
            onChange={(v) => set("escalation_policy", v)}
          />
        </Field>

        <Field label="Política de negociação" hint="Limite de desconto que a IA pode oferecer.">
          <OptionSelect
            testId="identity-negotiation-policy"
            value={data.negotiation_policy}
            options={NEGOTIATION_POLICIES}
            onChange={(v) => set("negotiation_policy", v)}
          />
        </Field>

        <Field label="Comportamento de upsell" hint="Como/quando sugerir serviços adicionais.">
          <OptionSelect
            testId="identity-upsell-behavior"
            value={data.upsell_behavior}
            options={UPSELL_BEHAVIORS}
            onChange={(v) => set("upsell_behavior", v)}
          />
        </Field>
      </div>

      <div className="flex items-center gap-3">
        <input
          id="use-emojis"
          type="checkbox"
          data-testid="identity-use-emojis"
          checked={!!data.use_emojis}
          onChange={(e) => set("use_emojis", e.target.checked)}
          className="h-4 w-4 rounded border-slate-300"
        />
        <label htmlFor="use-emojis" className="text-[13px] text-slate-700">
          Permitir que a IA use emojis nas respostas
        </label>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <Field label="Diferenciais" hint="O que vocês fazem melhor que ninguém.">
          <ChipList
            items={data.differentials}
            onAdd={(t) => set("differentials", [...(data.differentials || []), t])}
            onRemove={(i) => set("differentials", data.differentials.filter((_, idx) => idx !== i))}
            placeholder="Ex.: Mecânicos certificados BYD"
            testId="identity-differentials"
          />
        </Field>

        <Field label="Especialidades" hint="Áreas técnicas ou de produto.">
          <ChipList
            items={data.specialties}
            onAdd={(t) => set("specialties", [...(data.specialties || []), t])}
            onRemove={(i) => set("specialties", data.specialties.filter((_, idx) => idx !== i))}
            placeholder="Ex.: Híbridos, Elétricos"
            testId="identity-specialties"
          />
        </Field>

        <Field label="Frases preferidas" hint="A IA é incentivada a usar.">
          <ChipList
            items={data.preferred_phrases}
            onAdd={(t) => set("preferred_phrases", [...(data.preferred_phrases || []), t])}
            onRemove={(i) => set("preferred_phrases", data.preferred_phrases.filter((_, idx) => idx !== i))}
            placeholder="Ex.: Vou cuidar pra você"
            testId="identity-preferred-phrases"
          />
        </Field>

        <Field label="Frases proibidas" hint="A IA jamais usa.">
          <ChipList
            items={data.forbidden_phrases}
            onAdd={(t) => set("forbidden_phrases", [...(data.forbidden_phrases || []), t])}
            onRemove={(i) => set("forbidden_phrases", data.forbidden_phrases.filter((_, idx) => idx !== i))}
            placeholder="Ex.: valor a combinar"
            testId="identity-forbidden-phrases"
          />
        </Field>
      </div>

      <div className="pt-4 border-t border-slate-100 flex items-center justify-end">
        <button
          type="button"
          data-testid="identity-save"
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[13px] font-medium disabled:opacity-50"
        >
          <FloppyDisk size={14} weight="bold" />
          {saving ? "Salvando…" : "Salvar identidade"}
        </button>
      </div>
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// AgentIdentityPreview — live mirror of the customer's first message.
//
// Renders a phone-style bubble showing exactly the text the customer
// would receive on WhatsApp given the CURRENT identity settings (mode,
// agent_name, company_name). Re-renders instantly as the operator types
// — no backend roundtrip — by mirroring the rendering rules in
// backend `engines/reception.py`:
//   • _agent_signature_short(mode, name, company)
//   • build_smart_welcome_for_customer (case 4: known customer)
//
// Goal: reduce the "will it look weird?" hesitation when the operator
// flips between Marca da empresa / Assistente nomeada / Silencioso.
// ────────────────────────────────────────────────────────────────────────

/** Light client-side gender heuristic. Backend uses a curated lookup
 *  (`detect_gender`); the preview only needs to look right 95% of the
 *  time — the actual outbound is rendered server-side anyway. */
const _femaleHints = new Set([
  "ana", "bia", "beatriz", "carla", "clara", "fernanda", "julia",
  "júlia", "larissa", "letícia", "leticia", "maria", "marina",
  "rafaela", "sofia", "sophia", "valentina", "yara", "amanda",
]);
function _previewArticle(name) {
  const n = (name || "").trim().toLowerCase();
  if (!n) return "o ";
  if (_femaleHints.has(n)) return "a ";
  // Fallback heuristic: names ending in 'a' (unstressed) → female; the
  // most common false positives are Joshua/Lucca/etc. but those are
  // rare in Brazilian Portuguese.
  if (n.endsWith("a") && !n.endsWith("dua") && !n.endsWith("ua")) return "a ";
  return "o ";
}

function _previewGreeting() {
  const h = new Date().getHours();
  if (h >= 5 && h < 12) return "Bom dia";
  if (h >= 12 && h < 18) return "Boa tarde";
  return "Boa noite";
}

function _previewSignature(mode, name) {
  if (mode === "silent") return "";
  if (mode === "named_assistant") {
    const n = (name || "").trim() || "Assistente";
    return `Sou ${_previewArticle(n)}${n}.`;
  }
  return "Sou o assistente.";
}

function AgentIdentityPreview({ agent, setAg }) {
  const { profile } = useSector();
  const textareaRef = useRef(null);
  const [editing, setEditing] = useState(false);
  // v0.98 · Fev/2026 — CTA da preview vira sector-aware. Antes o texto
  // hardcodava "agendamento, orçamento ou cadastro de veículo" mesmo em
  // pet shop / clínica / salão etc. — quebrava a percepção de produto.
  const _sectorCTA = (() => {
    const code = (profile?.code || "automotive").toLowerCase();
    switch (code) {
      case "automotive":
        return "agendamento, orçamento ou cadastro do veículo";
      case "petshop":
        return "banho, tosa, consulta ou vacina do seu pet";
      case "clinic":
        return "agendamento de consulta, retorno ou dúvidas de saúde";
      case "salon":
        return "agendamento de horário, orçamento ou dúvidas";
      case "gym":
        return "avaliação, matrícula ou dúvidas sobre treino";
      case "real_estate":
        return "busca de imóvel, visita ou dúvidas sobre financiamento";
      case "hotel":
        return "reserva, disponibilidade ou dúvidas sobre a estadia";
      case "restaurant":
        return "reserva, pedido ou cardápio";
      case "tech_repair":
        return "orçamento, agendamento de conserto ou status de OS";
      case "insurance_broker":
        return "cotação, renovação ou dúvidas sobre a apólice";
      case "law_firm":
        return "consulta, andamento de processo ou dúvidas jurídicas";
      case "accounting_firm":
        return "envio de documentos, dúvidas fiscais ou agendamento";
      case "school":
        return "matrícula, dúvidas sobre aulas ou boletos";
      case "pharmacy":
        return "consultar medicamentos, tirar dúvidas ou fazer pedido";
      case "condominium":
        return "reservas, comunicados ou solicitações";
      default:
        return "agendamento, orçamento ou dúvidas";
    }
  })();
  const mode = agent.agent_display_mode || "brand_agent";
  const company = (agent.company_name || "").trim() || "sua empresa";
  const greeting = _previewGreeting();
  const sig = _previewSignature(mode, agent.agent_name);
  const sigShort = sig; // for the preview, signature_short == signature
  // Case 4 of build_smart_welcome_for_customer — the default "known
  // customer, no rich context" template that 80%+ of new conversations
  // hit. Mirrors the backend exactly.
  const sigLine = sig ? `${sig}\n` : "";
  const customTpl = (agent.smart_welcome_default_template || "").trim();
  let customerLine;
  if (customTpl) {
    // Render the custom template with the same variables the backend
    // exposes. `{nome_cliente}` is the PT-BR alias of `{first_name}`.
    let rendered = customTpl
      .replace(/\{greeting\}/g, greeting)
      .replace(/\{nome_cliente\}/g, "Rafael")
      .replace(/\{first_name\}/g, "Rafael")
      .replace(/\{company_name\}/g, company)
      .replace(/\{agent_signature_short\}/g, sigShort);
    // Drop empty signature line to avoid blank gap (silent mode).
    if (!sigShort) {
      rendered = rendered.replace(/^[ \t]*\n/gm, "");
    }
    customerLine = rendered;
  } else {
    customerLine =
      `${greeting}, Rafael! Que bom te ver novamente na *${company}* 👋\n` +
      `${sigLine}\n` +
      `Como vou te ajudar hoje? Posso ajudar com ${_sectorCTA}.`;
  }
  // Collapse triple newlines for silent-mode parity with the backend
  // `_collapse_signature_whitespace`.
  const tidied = customerLine.replace(/\n{3,}/g, "\n\n").trim();

  const modeLabel = {
    brand_agent: "Marca da empresa",
    named_assistant: "Assistente com nome próprio",
    silent: "Silencioso",
  }[mode];

  return (
    <div
      data-testid="identity-preview"
      className="mt-2 rounded-xl border border-slate-100 bg-slate-50/60 p-4"
    >
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-[11px] font-medium text-slate-700 uppercase tracking-wider">
          Assim seu cliente vai te ver
        </div>
        <span
          data-testid="identity-preview-mode-tag"
          className="text-[10px] font-medium text-slate-500 px-2 py-0.5 rounded-full bg-white border border-slate-200"
        >
          {modeLabel}
        </span>
      </div>

      {/* Phone-bubble — left-aligned green so it reads as an incoming
          WhatsApp message in the customer's inbox. */}
      <div className="flex">
        <div
          data-testid="identity-preview-bubble"
          className="max-w-[88%] rounded-2xl rounded-tl-md bg-emerald-50 border border-emerald-100 px-4 py-3 shadow-sm"
        >
          <div className="text-[12.5px] text-slate-800 leading-relaxed whitespace-pre-wrap">
            {tidied}
          </div>
          <div className="mt-1 text-[10px] text-slate-400 text-right">
            agora · ✓✓
          </div>
        </div>
      </div>

      <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
        Esta é a primeira mensagem que um cliente já conhecido recebe
        ao chamar no WhatsApp. Troque o modo acima para comparar.
      </p>

      {/* ── Editor da frase padrão ─────────────────────────────
          v0.99.6 · Fev/2026 — Frase padrão editável. Quando vazia,
          a IA usa o texto que aparece no balão acima. Ao salvar,
          o backend guarda em `inbound_settings.smart_welcome_default_template`
          e o `build_smart_welcome_for_customer` renderiza a versão
          custom em runtime. */}
      <div className="mt-4 pt-4 border-t border-slate-200/70">
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            data-testid="identity-preview-edit-toggle"
            className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-700 hover:text-slate-900 px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-slate-900 transition-colors"
          >
            <PencilSimple size={12} weight="bold" />
            Editar frase padrão
          </button>
        ) : (
          <div className="space-y-2" data-testid="identity-welcome-editor">
            <div className="flex items-center justify-between">
              <label className="text-[11px] font-medium text-slate-700 uppercase tracking-wider">
                Frase padrão de boas-vindas
              </label>
              <button
                type="button"
                onClick={() => {
                  setAg("smart_welcome_default_template", "");
                }}
                data-testid="identity-welcome-reset"
                className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-slate-900"
                title="Deixar vazio para usar a frase padrão do sistema"
              >
                <ArrowCounterClockwise size={11} weight="bold" />
                Restaurar padrão
              </button>
            </div>
            <textarea
              ref={textareaRef}
              value={agent.smart_welcome_default_template || ""}
              onChange={(e) => setAg("smart_welcome_default_template", e.target.value)}
              placeholder={
                `${greeting}, {nome_cliente}! Que bom te ver novamente na *{company_name}* 👋\n` +
                `{agent_signature_short}\n\n` +
                `Como vou te ajudar hoje? Posso ajudar com ${_sectorCTA}.`
              }
              rows={6}
              data-testid="identity-welcome-textarea"
              className="w-full px-3 py-2 rounded-xl border border-slate-200 text-[12.5px] font-mono leading-relaxed focus:border-slate-900 focus:outline-none resize-none bg-white"
            />
            <VariableInserter
              textareaRef={textareaRef}
              value={agent.smart_welcome_default_template || ""}
              onChange={(v) => setAg("smart_welcome_default_template", v)}
              variables={[
                { token: "{nome_cliente}", label: "Primeiro nome do cliente" },
                { token: "{company_name}", label: "Nome da empresa" },
                { token: "{greeting}", label: "Saudação (Bom dia/Boa tarde/Boa noite)" },
                { token: "{agent_signature_short}", label: "Assinatura do agente (ex.: Sou o Lucas)" },
              ]}
            />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              Escreva do seu jeito. Clique nas variáveis para inserir onde
              está o cursor. Deixe vazio para usar a frase padrão do sistema.
              Salvar em <span className="font-semibold">Salvar identidade</span> aplica pra todos os novos clientes.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

